import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { googleFormConfig } from '../config/googleFormConfig';
import './CallbackModal.css';

const CallbackModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneCode: '+91',
    phone: '',
    travelDate: '',
    travellerCount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare email content
      const emailSubject = `Travel Inquiry from ${formData.fullName}`;
      const emailBody = `
Travel Inquiry Details:

Name: ${formData.fullName}
Email: ${formData.email}
Phone: ${formData.phoneCode} ${formData.phone}
Travel Date: ${formData.travelDate}
Traveller Count: ${formData.travellerCount}
Message: ${formData.message || 'No additional message'}

Please contact this customer for their travel requirements.

Best regards,
Emrys Travel Team
      `;

      // Prepare Google Form data
      const fullPhone = `+${formData.phoneCode} ${formData.phone}`;
      
      try {
        // Method 1: Submit directly to Google Form
        await submitToGoogleForm({
          fullName: formData.fullName,
          email: formData.email,
          phone: fullPhone,
          travelDate: formData.travelDate,
          travellerCount: formData.travellerCount,
          message: formData.message || ''
        });
        
      } catch (submitError) {
        // Method 2: Fallback to pre-filled Google Form
        console.log('Direct submission failed, using pre-filled form:', submitError);
        
        const prefillUrl = createPrefilledGoogleForm();
        window.open(prefillUrl, '_blank');
      }
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phoneCode: '+91',
        phone: '',
        travelDate: '',
        travellerCount: '',
        message: ''
      });

      onClose();

    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to create pre-filled Google Form URL
  const createPrefilledGoogleForm = () => {
    const params = new URLSearchParams({
      [googleFormConfig.fieldMapping.fullName]: formData.fullName,
      [googleFormConfig.fieldMapping.email]: formData.email,
      [googleFormConfig.fieldMapping.phone]: `+${formData.phoneCode} ${formData.phone}`,
      [googleFormConfig.fieldMapping.travelDate]: formData.travelDate,
      [googleFormConfig.fieldMapping.travellerCount]: formData.travellerCount,
      [googleFormConfig.fieldMapping.message]: formData.message || ''
    });
    
    return `${googleFormConfig.formUrl}?${params.toString()}`;
  };

  // Helper function to submit directly to Google Form
  const submitToGoogleForm = async (data) => {
    const formDataToSubmit = new FormData();
    
    // Add each field to the form data
    Object.keys(googleFormConfig.fieldMapping).forEach(key => {
      const fieldId = googleFormConfig.fieldMapping[key];
      formDataToSubmit.append(fieldId, data[key] || '');
    });
    
    // Submit to Google Form
    const response = await fetch(googleFormConfig.actionUrl, {
      method: 'POST',
      body: formDataToSubmit,
      mode: 'no-cors' // Google Forms doesn't support CORS
    });
    
    return response;
  };

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Create portal to render modal at document root level
  const modalContent = (
    <div className="callback-modal-overlay" onClick={handleOverlayClick}>
      <div className="callback-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="modal-close-btn" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="modal-header">
          <h2>Request Callback</h2>
          <p>Connect with our travel experts</p>
        </div>

        <form className="callback-form" onSubmit={handleSubmit}>
          {/* Full Name */}
          <div className="form-group">
            <input
              type="text"
              name="fullName"
              placeholder="Full Name*"
              value={formData.fullName}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Email */}
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder="Email*"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Phone Number */}
          <div className="form-row">
            <div className="phone-code-group">
              <select
                name="phoneCode"
                value={formData.phoneCode}
                onChange={handleInputChange}
                className="phone-code-select"
              >
                <option value="+91">+91</option>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+971">+971</option>
              </select>
            </div>
            <div className="phone-number-group">
              <input
                type="tel"
                name="phone"
                placeholder="Your Phone*"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* Travel Date and Traveller Count */}
          <div className="form-row">
            <div className="form-group">
              <input
                type="date"
                name="travelDate"
                placeholder="Travel Date*"
                value={formData.travelDate}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <input
                type="number"
                name="travellerCount"
                placeholder="Traveller Count*"
                value={formData.travellerCount}
                onChange={handleInputChange}
                required
                min="1"
              />
            </div>
          </div>

          {/* Message */}
          <div className="form-group">
            <textarea
              name="message"
              placeholder="Message..."
              value={formData.message}
              onChange={handleInputChange}
              rows="4"
              className="message-textarea"
            />
          </div>

          {/* Submit Button */}
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Connect with an Expert'}
          </button>
        </form>
      </div>
    </div>
  );

  // Render modal using portal at document root
  return createPortal(modalContent, document.body);
};

export default CallbackModal;
